'use client'

import type { Capability } from '@brew/auth/rbac'
import { useOptionalBrandContext } from '@/contexts/brand-context'
import { useOptionalOrgContext, useOrgAccess } from '@/contexts/org-context'
import { CAPABILITY_DENIED_COPY } from '@/lib/auth/capability-copy'
import { canWithSnapshot } from '@/lib/auth/org-access'

/** The brand a gate targets: the named one, else the active one, else the org level. */
function useTargetBrandId(brandId?: string): string | null {
  const brand = useOptionalBrandContext()
  const target = brandId ?? brand?.brandId ?? ''
  return target.length > 0 ? target : null
}

/** The single yes/no every client control asks. */
export function useCan(capability: Capability, brandId?: string): boolean {
  const access = useOrgAccess()
  const target = useTargetBrandId(brandId)
  return canWithSnapshot(access, target, capability)
}

/** `useCan` plus the sentence a disabled control should show. */
export function useCapabilityGate(
  capability: Capability,
  brandId?: string
): { isAllowed: boolean; reason: string | null } {
  const isAllowed = useCan(capability, brandId)
  return {
    isAllowed,
    reason: isAllowed ? null : CAPABILITY_DENIED_COPY[capability],
  }
}

/**
 * `useCan` for components that also render on public surfaces (no org
 * context): `null` there — nothing to gate, the server refuses anyway.
 */
export function useOptionalCan(
  capability: Capability,
  brandId?: string
): boolean | null {
  const org = useOptionalOrgContext()
  const target = useTargetBrandId(brandId)
  return org ? canWithSnapshot(org.orgAccess, target, capability) : null
}
